import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const FAQ_DATA = [
  {
    category: "Getting Started",
    questions: [
      {
        q: "What is LeadGen AI?",
        a: "LeadGen AI is an AI-powered lead generation platform that helps you find business leads across Google Places, YouTube, LinkedIn, Instagram, Facebook, and 8+ other platforms. Just type what you need in plain English (or Hindi/Hinglish) and our AI finds, analyzes, and scores leads for you."
      },
      {
        q: "How does the free trial work?",
        a: "Every new user gets a FREE 2-day Premium trial with all features unlocked — unlimited searches, AI analysis, email finder, everything. No credit card required. After the trial ends, you're moved to the Basic plan and can upgrade anytime."
      },
      {
        q: "Do I need any technical skills?",
        a: "Not at all! Just type what you need like you'd talk to a friend. For example: 'Find me restaurants in Mumbai that need marketing help' or 'YouTube cooking channels with 10K subscribers'. Our AI handles everything else."
      }
    ]
  },
  {
    category: "Search & Features",
    questions: [
      {
        q: "What platforms can I search?",
        a: "LeadGen AI searches across multiple platforms:\n• Google Places — Local businesses worldwide (restaurants, salons, gyms, clinics, etc.)\n• YouTube — Channels and creators by niche, subscribers, and location\n• LinkedIn — Professional profiles and company pages\n• Instagram — Business and creator profiles\n• Facebook — Business pages\n• Twitter/X — Profiles\n• Justdial — Indian business listings\n• IndiaMART — Indian manufacturers and suppliers\n• Naukri — Job listings and profiles\n• Crunchbase — Startup and company data"
      },
      {
        q: "What is AI Chat Search?",
        a: "Instead of filling forms, you just type your request in natural language. Our AI understands complex queries like 'Find businesses that need digital marketing in Tier 2 Indian cities' and automatically plans the right search strategy. It works in English, Hindi, and Hinglish."
      },
      {
        q: "What is Multi-Category Search?",
        a: "When you search for something like 'find me clients for my marketing agency', our AI automatically searches across multiple business categories (restaurants, salons, gyms, coaches, etc.) in multiple cities simultaneously. This gives you diverse, high-quality leads instead of just one type."
      },
      {
        q: "What is AI Lead Scoring?",
        a: "Every lead gets automatically scored as Hot 🔥 (70-100), Warm 🟡 (40-69), or Cold 🔵 (0-39) based on 8 signals including website presence, email availability, social media activity, rating, and review count. Hot leads are most likely to need your services."
      },
      {
        q: "What is AI Prospect Analysis?",
        a: "For Premium users, our AI analyzes each lead and provides:\n• Why They Need You — A specific reason this business needs your service (e.g., 'No website, only 3 reviews — invisible online')\n• Approach Suggestion — How to pitch them (e.g., 'Offer a free Google Business audit')\n• Prospect Score — 0-100 rating of how much they need your service\nThis is available for 'find clients' type searches."
      },
      {
        q: "How does the Email Finder work?",
        a: "Our bots visit each business's website and scan for email addresses, phone numbers, and social media links. We check the homepage, contact page, about page, and 15+ other common pages. We extract emails, phones, and links for Facebook, Instagram, Twitter, LinkedIn, YouTube, Pinterest, Yelp, and WhatsApp."
      }
    ]
  },
  {
    category: "Plans & Pricing",
    questions: [
      {
        q: "What plans are available?",
        a: "We offer three plans:\n• Basic (₹399/month) — 50 leads/day, all search platforms, AI scoring, CSV export\n• Standard (₹999/month) — 500 leads/day, multi-category search, Excel export\n• Premium (₹2499/month) — 1000 leads/day (unlimited), email finder, AI prospect analysis, social media extraction, all features\nAll new users start with a FREE 2-day Premium trial."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major Indian payment methods through Razorpay — UPI, credit cards, debit cards, net banking, and mobile wallets."
      },
      {
        q: "Can I upgrade or downgrade my plan?",
        a: "Yes! You can switch plans anytime from the Pricing page. Upgrades take effect immediately. Downgrades apply at the end of your billing period."
      },
      {
        q: "Is there a refund policy?",
        a: "Yes, we offer a full refund within 7 days of purchase if you're not satisfied. Contact us at ankulpal983@gmail.com."
      }
    ]
  },
  {
    category: "Data & Privacy",
    questions: [
      {
        q: "How many countries do you support?",
        a: "We support 82 countries with pre-mapped cities including US, UK, India, Canada, Australia, Germany, France, UAE, Singapore, and many more. You can search for businesses in any city worldwide."
      },
      {
        q: "Is my data secure?",
        a: "Absolutely. We use Supabase for authentication with Google and GitHub login options. All data is encrypted. We use bank-grade security with Row Level Security policies. Your search data and leads are private and only accessible to your account."
      },
      {
        q: "Can I export my leads?",
        a: "Yes! Depending on your plan, you can export leads as CSV or Excel files. The export includes all data — business name, phone, email, website, social media links, rating, reviews, AI scores, and prospect insights."
      },
      {
        q: "What is Freelancer Mode?",
        a: "Freelancer Mode is designed for freelancers and agencies. You complete a 4-step onboarding where you set your service type (Video Editor, Web Developer, etc.), skills, pricing, and target clients. This helps our AI understand your business and suggest better leads tailored to your specific service."
      }
    ]
  },
  {
    category: "Troubleshooting",
    questions: [
      {
        q: "Why am I getting irrelevant leads?",
        a: "Try being more specific in your search. For example, instead of 'businesses in India', try 'restaurants in Mumbai that need marketing help'. Our AI works best with specific queries that include your intent, target audience, and location."
      },
      {
        q: "What if the email finder doesn't find emails?",
        a: "Not all businesses have publicly available emails on their websites. The email finder works by scanning websites, so businesses without websites or with email-protected pages may not have emails found. Try using the phone number or social media links instead."
      },
      {
        q: "My search is taking too long. What should I do?",
        a: "Multi-category searches across multiple cities may take 30-60 seconds because they run multiple searches sequentially. This is normal. Single-category searches typically complete in 10-20 seconds."
      }
    ]
  }
];

const FAQAccordionItem = ({ faq, isOpen, onClick }) => {
  return (
    <div className={`border border-white/10/50 rounded-xl overflow-hidden transition-all duration-300 ${isOpen ? 'bg-zinc-800/80 shadow-lg' : 'bg-zinc-900/40 hover:bg-zinc-800/50'}`}>
      <button 
        className="w-full text-left px-6 py-4 flex justify-between items-center focus:outline-none"
        onClick={onClick}
      >
        <span className="font-semibold text-white pr-8">{faq.q}</span>
        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'bg-blue-500/20 text-blue-400 rotate-180' : 'bg-slate-800 text-gray-400'}`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div 
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] pb-5 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <p className="text-gray-400 leading-relaxed border-t border-white/10/50 pt-4 mt-2 text-sm whitespace-pre-line">
          {faq.a}
        </p>
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState(`0-0`); // Format: categoryIndex-questionIndex

  // Filter FAQs based on search
  const filteredData = FAQ_DATA.map(category => {
    return {
      ...category,
      questions: category.questions.filter(
        q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) || 
             q.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    };
  }).filter(category => category.questions.length > 0);

  const toggleAccordion = (catIndex, qIndex) => {
    const id = `${catIndex}-${qIndex}`;
    setOpenIndex(prev => prev === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 text-gray-300 py-16">
      <Helmet>
        <title>FAQ — LeadGen AI | AI Lead Generation Questions Answered</title>
        <meta name="description" content="Frequently asked questions about LeadGen AI. Learn about AI chat search, lead scoring, email finder, YouTube search, multi-platform search, pricing, and more." />
        <link rel="canonical" href="https://leadgenai.in/faq" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header & Search */}
        <div className="text-center mb-12 animate-slide-up">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-400 mb-8">
            Have a question? We're here to help.
          </p>
          
          <div className="relative max-w-xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search for answers..."
              className="w-full bg-zinc-900 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow shadow-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* FAQs */}
        <div className="space-y-12 animate-fade-in">
          {filteredData.length === 0 ? (
            <div className="text-center py-16 bg-zinc-900/40 rounded-2xl border border-white/10">
              <h3 className="text-2xl text-gray-400 mb-2">No results found</h3>
              <p className="text-gray-500">We couldn't find any FAQs matching "{searchQuery}"</p>
            </div>
          ) : (
            filteredData.map((category, catIndex) => (
              <div key={catIndex} className="space-y-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <span className="w-8 h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mr-3"></span>
                  {category.category}
                </h2>
                <div className="space-y-4 pl-2 md:pl-11">
                  {category.questions.map((faq, qIndex) => (
                    <FAQAccordionItem 
                      key={qIndex} 
                      faq={faq} 
                      isOpen={openIndex === `${catIndex}-${qIndex}`}
                      onClick={() => toggleAccordion(catIndex, qIndex)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Support CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/20 rounded-2xl p-8 text-center animate-fade-in">
          <h3 className="text-2xl font-bold text-white mb-2">Still need help?</h3>
          <p className="text-gray-400 mb-6">Our support team is always ready to assist you with any questions.</p>
          <a href="/contact" className="inline-block bg-white text-dark-900 hover:bg-slate-200 font-semibold py-3 px-8 rounded-xl transition-colors">
            Contact Support
          </a>
        </div>

      </div>
    </div>
  );
};

export default FAQPage;
